import { FC, useState } from 'react'
import { ApplicationFrame } from '../../components/ApplicationFrame'
import {
  Button,
  TextField,
  Typography,
  Box,
  Snackbar,
  Card,
  CardMedia,
  CardContent,
  Link,
} from '@mui/material'
import {
  useClientApiCall,
  useClientApiMutation,
} from '../../hooks/useClientApiCall'
import { useSearchParams } from 'react-router-dom'
import mealzoLogoAsset from '../../assets/mealzo-order-injector/mealzo-logo.jpeg'

/**
 * Uber Eats Application
 */
export const MealzoOrderInjector: FC = () => {
  const [params] = useSearchParams()
  const session = params.get('session')!
  const connection = useClientApiCall(session, 'connection')
  const connect = useClientApiMutation(session, 'connect')
  const disconnect = useClientApiMutation(session, 'disconnect')
  const [storeUrl, setStoreUrl] = useState('')
  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const isStoreAlreadyConnected = connection.data?.mealzoStoreId !== undefined

  const doConnect = async () => {
    try {
      await connect.mutateAsync({ storeUrl })
      await connection.refetch()
      if (isStoreAlreadyConnected) {
        setIsConnected(true)
        setToastMessage('Connection Successful, store connected!')
        setToastOpen(true)
      }
    } catch (error) {
      console.error(error)
      setToastMessage('Connection failed! Please try again.')
      setToastOpen(true)
    }
  }

  const doDisconnect = async () => {
    try {
      await disconnect.mutateAsync({})
      setIsConnected(false)
      await connection.refetch()
      if (isStoreAlreadyConnected) {
        setIsConnected(true)
        setToastMessage('Disconnection Successful, store disconnected!')
        setToastOpen(true)
      }
    } catch (error) {
      console.error(error)
      setToastMessage('Connection failed! Please try again.')
      setToastOpen(true)
    }
  }

  const handleCloseToast = () => {
    setToastOpen(false)
  }

  return (
    <ApplicationFrame heroImage={mealzoLogoAsset} backgroundColor={'#fe5105'}>
      {/* Title */}
      <Typography
        variant="h4"
        align="center"
        sx={{ p: 2, fontWeight: 'bold', color: '#000' }}
      >
        Mealzo Store Connector
      </Typography>

      {/* Store Information */}
      {isStoreAlreadyConnected && (
        <>
          <Card
            sx={{
              maxWidth: 500,
              margin: '30px auto',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <CardMedia
              component="img"
              alt="Store Logo"
              height="180"
              image={connection.data?.mealzoStoreLogo}
              sx={{ objectFit: 'contain', backgroundColor: '#f0f0f0' }}
            />
            <CardContent
              sx={{ textAlign: 'center', backgroundColor: '#f9f9f9' }}
            >
              <Typography variant="h6" align="center">
                <Link
                  href={connection.data?.mealzoStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  {connection.data?.locationName}
                </Link>
              </Typography>
            </CardContent>
          </Card>
          <Button
            onClick={doDisconnect}
            variant="contained"
            color="primary"
            size="large"
            sx={{
              width: 'fit-content',
              padding: '6px 16px',
              margin: '10px auto',
            }}
          >
            Disconnect
          </Button>
        </>
      )}

      {/* Input and Button */}
      {!isStoreAlreadyConnected && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 3,
          }}
        >
          <TextField
            label="Mealzo store URL"
            value={storeUrl}
            placeholder="https://mealzo.co.uk/{identity}/menu"
            onChange={(e) => setStoreUrl(e.target.value)}
            fullWidth
            variant="outlined"
          />

          <Button
            onClick={doConnect}
            disabled={connect.isLoading || !storeUrl}
            variant="contained"
            color="primary"
            size="large"
            sx={{ width: 'fit-content', padding: '6px 16px' }}
          >
            Connect
          </Button>
        </Box>
      )}

      {/* Toast Notification */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        message={toastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: toastMessage.includes('Successful')
              ? '#4CAF50'
              : '#f44336',
            color: '#fff',
            fontWeight: 'bold',
          },
        }}
      />
    </ApplicationFrame>
  )
}
